import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  UncontrolledAlert
} from "reactstrap";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import Dropzone from "react-dropzone";
import { usePocketBase } from "../../../PocketBaseContext";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

const AddCustomer = () => {
  const navigate = useNavigate();
  const pb = usePocketBase();
  const [personnel, setPersonnel] = useState([]);
  const [alert, setAlert] = useState({ show: false, color: "", message: "" });
  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    fetchPersonnel();
  }, []);

  const fetchPersonnel = async () => {
    try {
      const resultList = await pb.collection('personel').getList(1, 50, {
        sort: 'name',
      });
      setPersonnel(resultList.items);
    } catch (error) {
      console.error("Error fetching personnel:", error);
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Müşteri adı gereklidir"),
    kurumsallik: Yup.string().required("Müşteri tipi gereklidir"),
    // Kurumsal müşteri alanları
    tax_number: Yup.string().when("kurumsallik", (kurumsallik, schema) => {
      return kurumsallik === "kurumsal" 
        ? schema.required("Vergi numarası gereklidir")
        : schema;
    }),
    tax_office: Yup.string().when("kurumsallik", (kurumsallik, schema) => {
      return kurumsallik === "kurumsal"
        ? schema.required("Vergi dairesi gereklidir")
        : schema;
    }),
    // Bireysel müşteri alanları
    tc_number: Yup.string().when("kurumsallik", (kurumsallik, schema) => {
      return kurumsallik === "bireysel"
        ? schema.required("T.C. Kimlik numarası gereklidir").length(11, "T.C. Kimlik numarası 11 haneli olmalıdır")
        : schema;
    }),
    birth_date: Yup.date().when("kurumsallik", (kurumsallik, schema) => {
      return kurumsallik === "bireysel"
        ? schema.nullable().transform((curr, orig) => orig === '' ? null : curr)
        : schema;
    }),
    nationality: Yup.string().when("kurumsallik", (kurumsallik, schema) => {
      return kurumsallik === "bireysel"
        ? schema
        : schema;
    }),
    gender: Yup.string().when("kurumsallik", (kurumsallik, schema) => {
      return kurumsallik === "bireysel"
        ? schema
        : schema;
    }),
    // Ortak alanlar
    customer_address: Yup.string().required("Adres gereklidir"),
    customer_contact: Yup.array(),
    customer_brand_description: Yup.string()
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      kurumsallik: "",
      // Kurumsal müşteri alanları
      tax_number: "",
      tax_office: "",
      // Bireysel müşteri alanları
      tc_number: "",
      birth_date: "",
      nationality: "",
      gender: "",
      // Ortak alanlar
      customer_address: "",
      customer_contact: [],
      customer_brand_description: "",
      pictures: []
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        Object.keys(values).forEach(key => {
          if (key === 'customer_contact') {
            formData.append(key, JSON.stringify(values[key]));
          } else if (key === 'pictures' && uploadedFiles.length > 0) {
            uploadedFiles.forEach(file => {
              formData.append('pictures', file);
            });
          } else {
            formData.append(key, values[key]);
          }
        });

        await pb.collection('customers').create(formData);
        
        setAlert({
          show: true,
          color: "success",
          message: "Müşteri başarıyla eklendi"
        });

        setTimeout(() => {
          navigate("/reform-customers/list");
        }, 1500);
      } catch (error) {
        console.error("Error creating customer:", error);
        setAlert({
          show: true,
          color: "danger",
          message: "Müşteri eklenirken bir hata oluştu"
        });
      }
    },
  });

  const handleFileUpload = (files) => {
    setUploadedFiles(files);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Müşteri Ekle" pageTitle="Müşteriler" />
        
        {alert.show && (
          <UncontrolledAlert color={alert.color}>
            {alert.message}
          </UncontrolledAlert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Yeni Müşteri Bilgileri</h4>
              </CardHeader>
              <CardBody>
                <Form onSubmit={formik.handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="name">Müşteri Adı <span className="text-danger">*</span></Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.name}
                          invalid={formik.touched.name && formik.errors.name}
                        />
                        {formik.touched.name && formik.errors.name && (
                          <div className="text-danger">{formik.errors.name}</div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="kurumsallik">Müşteri Tipi <span className="text-danger">*</span></Label>
                        <Select
                          id="kurumsallik"
                          name="kurumsallik"
                          value={formik.values.kurumsallik ? 
                            { value: formik.values.kurumsallik, label: formik.values.kurumsallik === 'kurumsal' ? 'Kurumsal' : 'Bireysel' } : null}
                          onChange={(selected) => formik.setFieldValue('kurumsallik', selected.value)}
                          options={[
                            { value: 'kurumsal', label: 'Kurumsal' },
                            { value: 'bireysel', label: 'Bireysel' }
                          ]}
                        />
                        {formik.touched.kurumsallik && formik.errors.kurumsallik && (
                          <div className="text-danger">{formik.errors.kurumsallik}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Kurumsal Müşteri Alanları */}
                  {formik.values.kurumsallik === 'kurumsal' && (
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="tax_number">Vergi Numarası <span className="text-danger">*</span></Label>
                          <Input
                            id="tax_number"
                            name="tax_number"
                            type="text"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.tax_number}
                            invalid={formik.touched.tax_number && formik.errors.tax_number}
                          />
                          {formik.touched.tax_number && formik.errors.tax_number && (
                            <div className="text-danger">{formik.errors.tax_number}</div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="tax_office">Vergi Dairesi <span className="text-danger">*</span></Label>
                          <Input
                            id="tax_office"
                            name="tax_office"
                            type="text"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.tax_office}
                            invalid={formik.touched.tax_office && formik.errors.tax_office}
                          />
                          {formik.touched.tax_office && formik.errors.tax_office && (
                            <div className="text-danger">{formik.errors.tax_office}</div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  {/* Bireysel Müşteri Alanları */}
                  {formik.values.kurumsallik === 'bireysel' && (
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="tc_number">T.C. Kimlik No <span className="text-danger">*</span></Label>
                          <Input
                            id="tc_number"
                            name="tc_number"
                            type="text"
                            maxLength="11"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.tc_number}
                            invalid={formik.touched.tc_number && formik.errors.tc_number}
                          />
                          {formik.touched.tc_number && formik.errors.tc_number && (
                            <div className="text-danger">{formik.errors.tc_number}</div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="birth_date">Doğum Tarihi</Label>
                          <Input
                            id="birth_date"
                            name="birth_date"
                            type="date"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.birth_date}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="nationality">Uyruk</Label>
                          <Input
                            id="nationality"
                            name="nationality"
                            type="text"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.nationality}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="gender">Cinsiyet</Label>
                          <Input
                            id="gender"
                            name="gender"
                            type="select"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.gender}
                          >
                            <option value="">Seçiniz</option>
                            <option value="Erkek">Erkek</option>
                            <option value="Kadın">Kadın</option>
                            <option value="Diğer">Diğer</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  <Row>
                    <Col md={12}>
                      <FormGroup>
                        <Label for="customer_address">Adres <span className="text-danger">*</span></Label>
                        <Input
                          id="customer_address"
                          name="customer_address"
                          type="textarea"
                          rows="3"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.customer_address}
                          invalid={formik.touched.customer_address && formik.errors.customer_address}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <FormGroup>
                        <Label for="customer_contact">İlgili Personel</Label>
                        <Select
                          isMulti
                          id="customer_contact"
                          name="customer_contact"
                          value={personnel.filter(person => 
                            formik.values.customer_contact.includes(person.id)
                          ).map(person => ({
                            value: person.id,
                            label: `${person.name} ${person.surname}`
                          }))}
                          onChange={(selected) => {
                            formik.setFieldValue(
                              'customer_contact',
                              selected ? selected.map(option => option.value) : []
                            );
                          }}
                          options={personnel.map(person => ({
                            value: person.id,
                            label: `${person.name} ${person.surname}`
                          }))}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <FormGroup>
                        <Label for="customer_brand_description">Açıklama</Label>
                        <Input
                          id="customer_brand_description"
                          name="customer_brand_description"
                          type="textarea"
                          rows="4"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.customer_brand_description}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <FormGroup>
                        <Label>Dosyalar</Label>
                        <Dropzone onDrop={handleFileUpload}>
                          {({getRootProps, getInputProps}) => (
                            <div {...getRootProps()} className="dropzone dz-clickable">
                              <input {...getInputProps()} />
                              <div className="dz-message needsclick">
                                <div className="mb-3">
                                  <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                                </div>
                                <h4>Dosyaları buraya sürükleyin veya yüklemek için tıklayın.</h4>
                              </div>
                            </div>
                          )}
                        </Dropzone>
                        <div className="list-unstyled mb-0 mt-3">
                          {uploadedFiles.map((file, index) => (
                            <div key={index} className="mt-2">
                              <i className="ri-file-text-line"></i> {file.name}
                            </div>
                          ))}
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="text-end mt-4">
                    <Button type="button" color="light" className="me-2" onClick={() => navigate("/reform-customers/list")}>
                      İptal
                    </Button>
                    <Button type="submit" color="primary">
                      Müşteri Ekle
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AddCustomer;